<template>
    <div class="header bg-primary d-flex justify-content-between align-items-center">
        <div class="header-start">
            <button class="header-action" type="button" v-if="canGoBack" @click="onBack()">
                <i class="fa fa-long-arrow-alt-left text-light" />
            </button>
            <span class="text-light small fw-bold" v-else>{{ version }}</span>
        </div>
        <h1 class="header-title text-light m-0">{{ title }}</h1>
        <div class="header-end">
            <button class="header-action" type="button" @click="onLogout()" v-if="$route.name === 'Buildings'">
                <i class="fa fa-door-open text-light" />
            </button>
            <button class="header-action" type="button" @click="onHome()" v-else>
                <i class="fa fa-home text-light" />
            </button>
        </div>
    </div>
    <template v-if="isOffline">
        <template v-if="offlineModeIsPermitted">
            <el-alert class="offline-alert"
                      title="App is running in offline mode."
                      type="info"
                      :closable="false"
                      v-if="!hasPendingChanges" />
            <el-alert class="offline-alert"
                      :title="`You have ${pendingChangesCount} unsaved changes. re-establish connectivity to save.`"
                      type="warning"
                      :closable="false"
                      v-else-if="hasPendingChanges" />
        </template>
        <template v-else>
            <el-alert class="offline-alert"
                      title="You are offline. An internet connection is required for correct operation."
                      type="error"
                      :closable="false"/>
        </template>
    </template>
    <el-alert class="offline-alert"
              :title="`Saving ${pendingChangesCount} changes...`"
              type="warning"
              :closable="false"
              v-else-if="hasPendingChanges" />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {rootContext, userContext} from "@/store";

export default defineComponent({
    name: "AppHeader",
    data(): { version: string | undefined } {
        return {
            version: process.env.VUE_APP_VERSION
        };
    },
    computed: {
        canGoBack(): boolean {
            return this.$route.name !== "Home" && this.$route.name !== "Buildings";
        },
        backRouteName(): string | null {
            const backRouteName = this.$route.meta.backRouteName;

            return typeof backRouteName === "string" ? backRouteName : null;
        },
        hasPendingChanges(): boolean {
            return this.pendingChangesCount > 0;
        },
        isOffline(): boolean {
            return rootContext.state.isOffline;
        },
        title(): string {
            return this.$route.meta.title;
        },
        pendingChangesCount(): number {
            return rootContext.state.commands.length;
        },
        offlineModeIsPermitted(): boolean {
            return process.env.VUE_APP_PERMIT_OFFLINE_MODE === "true";
        }
    },
    methods: {
        async onBack(): Promise<void> {
            if (window.history.state?.useBrowserBack === true) {
                this.$router.back();
                return;
            }

            if (this.backRouteName) {
                await this.$router.replace({name: this.backRouteName});
                return;
            }

            this.$router.back();
        },
        async onHome(): Promise<void> {
            await this.$router.push({name: "Buildings"});
        },
        async onLogout(): Promise<void> {
            await userContext.actions.logout();
            await this.$router.push({name: "Login"});
        }
    }
});
</script>

<style lang="scss" scoped>
@import "../../scss/variables.scss";

.header {
    --header-shadow: 0 10px 26px rgba(15, 18, 24, 0.2);
    align-items: center;
    background-color: $primary;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    box-shadow: var(--header-shadow);
    height: 64px;
    padding: 0 20px;
    position: relative;
    overflow: hidden;
}

.header::before {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.1), transparent 55%);
    content: "";
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 45%;
}

.header::after {
    background: linear-gradient(90deg, transparent 0%, $primary-dark 0%, $primary-dark 100%);
    content: "";
    height: 6px;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    bottom: 0;
}
.header-start,
.header-end {
    align-items: center;
    display: flex;
    gap: 12px;
    position: relative;
    z-index: 1;
}

.header-title {
    font-size: 1.35em;
    font-weight: 600;
    letter-spacing: 0.05em;
    position: relative;
    z-index: 1;
}


.header-action {
    align-items: center;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0;
    color: $light;
    cursor: pointer;
    display: inline-flex;
    height: 40px;
    justify-content: center;
    padding: 0;
    transition: transform 0.15s ease, background-color 0.2s ease, border-color 0.2s ease;
    width: 40px;
}

.header-action i.fa {
    font-size: 1.2rem;
}

.header-action:hover {
    background: rgba(255, 255, 255, 0.22);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-1px);
}

.header-action:active {
    transform: translateY(0);
}

.header-action:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.45);
    outline: none;
}

.fa-door-open {
    font-size: 1.5em;
}

.fa-home {
    font-size: 1.7em;
}

.fa-long-arrow-alt-left {
    font-size: 1.7em;
}

.header i.fa {
    line-height: 1;
}
</style>
